
import { forwardRef, useState } from 'react';
import { cn } from "@/lib/utils";
import { AlertCircle, Check } from 'lucide-react';

interface EnhancedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  success?: boolean;
  helper?: string;
}

const EnhancedInput = forwardRef<HTMLInputElement, EnhancedInputProps>(
  ({ className, type, label, error, success, helper, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
      <div className="space-y-2">
        {label && (
          <label className={cn(
            "text-sm font-medium transition-colors",
            error ? "text-red-500" : success ? "text-green-500" : "text-white",
            isFocused && !error && !success && "text-garage-blue"
          )}>
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          <input
            type={type}
            className={cn(
              "flex h-12 w-full rounded-md border border-garage-blue bg-garage-blue text-white px-3 py-2 text-sm transition-all duration-200",
              "placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              error 
                ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" 
                : success
                ? "border-green-500 focus:border-green-500 focus:ring-green-500/20"
                : "focus:border-garage-blue focus:ring-garage-blue/20",
              isFocused && "shadow-sm",
              className
            )}
            ref={ref}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />
          
          {/* Status icons */}
          {(error || success) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {error ? (
                <AlertCircle className="h-4 w-4 text-red-500" />
              ) : success ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : null}
            </div>
          )}
        </div>
        
        {/* Helper text or error message */}
        {(error || helper) && (
          <p className={cn(
            "text-xs transition-colors",
            error ? "text-red-500" : "text-gray-500"
          )}>
            {error || helper}
          </p>
        )}
      </div>
    );
  }
);

EnhancedInput.displayName = "EnhancedInput";

export default EnhancedInput;
